// src/app/services/taxExtensions/index.ts
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from '../index';
import { IUser } from '../user';

export interface ITaxExtension {
  id: number;
  year: number;
  type: 'business' | 'individual';
  status: 'in_progress' | 'completed' | 'submitted' | 'cancelled';
  payment_status: 'unpaid' | 'paid';
  title: string;
  creator: IUser;
  business_id?: number;
  business?: { id: number; name: string };
  created_at: string;
  price?: number;
  extension_price?: number;
  data?: ITaxExtensionForm;
}

export interface ITaxExtensionForm {
  type: 'individual' | 'business';
  business_id?: number;

  year: number;
  firstName: string;
  lastName: string;
  ssn: string;
  phoneNumber: string;
  email: string;

  isMarried?: 'yes' | 'no';
  spouseFirstName?: string;
  spouseLastName?: string;
  spouseSSN?: string;

  haveDependents?: 'yes' | 'no';
  depenendentsCountUnder16?: number;
  otherdepenendentsCount?: number;

  address: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
  liveOutsideOfUs: boolean;
  otherCountry: string;
  provinceCounty: string;
  collegeTuitionAmount: number;
  businessType?: string;
  expectedBusinessProfitOrLoss: number;
  expectedBusinessProfitOrLossFromPersonallyOwnedRentalProperty: number;
  expectedIraOrPensionDistribution: number;
  federalWithholdingsOnIra: number;
  stateWithholdingsOnIra: number;
  taxpayerTotalSocialSecurityReceivedSoFarThisYear: number;
  spouseTotalSocialSecurityReceivedSoFarThisYear: number;
  taxpayerTotalUnemploymentCompensationReceivedSoFarThisYear: number;
  spouseTotalUnemploymentCompensationReceivedSoFarThisYear: number;
  otherIncome: number;

  federalEstimatedTaxesAlreadyPaidSoFarThisYear: number;
  stateEstimatedTaxesAlreadyPaidSoFarThisYear: number;
  notes: string;
  reachPhoneNumber: string;
  whatIsTheBestTimeToReachYouAtThisNumber: string;
  agreement: boolean;

  latestPaySub: {
    files: {
      file_url: string;
      title: string;
    };
  }[];
  priorYearTaxReturn: {
    files: {
      file_url: string;
      title: string;
    };
  }[];

  // Additional fields
  completedFederalReturn?: string;
  filingStatus?: string;
  priorYearAGI?: number;
  expectOweTaxes?: string;
  estimatedPaymentsMade?: string;
  estimatedPaymentsAmount?: number;
  estimatedTaxLiability?: number;
  stateTaxExtensionRequired?: string;
  stateTaxExtensionState?: string;
  extraordinaryCircumstances?: string;
  circumstancesDescription?: string;
  assistanceWithTaxPayments?: string;
  extensionFilingMethod?: string;
  contactInformation?: string;
}

export const TaxExtensionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTaxExtensions: builder.query<
      IPaginatedRepsonse<ITaxExtension>,
      IGetQueryParams<{
        status?: ITaxExtension['status'];
        client_id?: number;
        business_id?: number;
      }>
    >({
      query: (params) => 'tax_extensions_requests' + buildQueryParams(params),
    }),

    getTaxExtensionById: builder.query<ITaxExtension, number>({
      query: (id) => `/tax_extensions_requests/${id}`,
      transformResponse: (response: { request: ITaxExtension }) =>
        response.request,
    }),

    createTaxExtension: builder.mutation<
      Partial<ITaxExtension>,
      Partial<ITaxExtension>
    >({
      query: (request) => {
        return {
          url: `/tax_extensions_requests`,
          method: 'POST',
          body: { request },
        };
      },
      transformResponse: (response: { request: ITaxExtension }) =>
        response.request,
    }),

    updateTaxExtension: builder.mutation<ITaxExtension, Partial<ITaxExtension>>(
      {
        query: (request: Partial<ITaxExtension>) => {
          const body = {
            request,
          };
          return {
            url: `/tax_extensions_requests/${body.request.id}`,
            method: 'PUT',
            body,
          };
        },
        transformResponse: (response: { request: ITaxExtension }) =>
          response.request,

        onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => {
          return queryFulfilled.then((response) => {
            dispatch(
              TaxExtensionApi.util.updateQueryData(
                'getTaxExtensionById',
                id!,
                (draft) => Object.assign(draft, response.data),
              ),
            );
          });
        },
      },
    ),

    updateTaxExtensionStatus: builder.mutation<
      ITaxExtension,
      { id: number; status: ITaxExtension['status'] }
    >({
      query: ({ id, status }) => {
        let route = '';
        if (status === 'in_progress') route = 'set_as_in_progress';
        if (status === 'completed') route = 'complete';
        if (status === 'cancelled') route = 'cancel';

        return {
          url: `/tax_extensions_requests/${id}/${route}`,
          method: 'POST',
        };
      },
      transformResponse: (response: { request: ITaxExtension }) =>
        response.request,

      onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => {
        return queryFulfilled.then((response) => {
          dispatch(
            TaxExtensionApi.util.updateQueryData(
              'getTaxExtensionById',
              id!,
              (draft) => Object.assign(draft, response.data),
            ),
          );
        });
      },
    }),

    deleteTaxExtension: builder.mutation<ITaxExtension, number>({
      query: (id) => ({
        url: `/tax_extensions_requests/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { task: ITaxExtension }) => response.task,
    }),

    payTaxExtension: builder.mutation<{ checkout_url: string }, string>({
      query: (requestId) => ({
        url: `/tax_extensions_requests/${requestId}/pay`,
        method: 'POST',
      }),
      transformResponse: (response: { checkout_url: string }) => response,
    }),
  }),
});

// ----------------------------------------------------------------------
// ADD THE AUTO-GENERATED HOOKS HERE
// by destructuring the endpoints, we get the normal & lazy queries & mutations
// ----------------------------------------------------------------------
export const {
  // We ALREADY had:
  useUpdateTaxExtensionStatusMutation,
  useLazyGetTaxExtensionsQuery,
  useUpdateTaxExtensionMutation,
  useGetTaxExtensionByIdQuery,
  useDeleteTaxExtensionMutation,
  useCreateTaxExtensionMutation,
  usePayTaxExtensionMutation,

  // ADD THE NORMAL HOOK:
  // by default, RTK Query calls it "useGetTaxExtensionsQuery" for builder.query
  useGetTaxExtensionsQuery,
} = TaxExtensionApi;
